<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { mdiArchiveEye, mdiPlus } from "@mdi/js"
  import { untrack } from "svelte"
  import PropertyUnitsTable, {
    type PropertyUnitRow,
  } from "../../../../../../_shared/_modules/property-units-table"
  import PropertyUnitOverlay, {
    type UnitFormModel,
  } from "../../../../../../_shared/_modules/property-unit-overlay"
  import UnitContactAssignmentOverlay from "./_modules/unit-contact-assignment-overlay"
  import {
    createPropertyUnit,
    updatePropertyUnit,
    archivePropertyUnitsBatch,
    restorePropertyUnitsBatch,
  } from "../../../../../../../../../../../../api/kehrblatt/unit-registry/property-units"
  import {
    deletePropertyCustomer,
    updatePropertyCustomer,
  } from "../../../../../../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { spaNavigate } from "../../../../../../../../../../../../_shared/_logic/navigation"
  import {
    registryArchiveIssueMessage,
    registryArchiveProcessed,
  } from "../../../../../../../../../../_shared/_logic/batch-results"
  import { replaceUrlWithoutRoutePublish } from "../../../../../../../../../../_shared/_logic/routes-root-silent-url-replace"
  import {
    CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
    customerRegistryActionTooltips,
  } from "../../../../../../../../_shared/_logic/customer-registry-action-tooltips"
  type Props = {
    property: Property | null
    organizationId: string
    customers?: Customer[]
    propertyLinks?: PropertyCustomer[]
    canEdit?: boolean
    onChanged?: (payload: { property: Property; links: PropertyCustomer[] }) => void
  }

  let {
    property = null,
    organizationId,
    customers = [],
    propertyLinks = [],
    canEdit = true,
    onChanged = () => {},
  }: Props = $props()

  const syncpropertyLinks = () =>
    onChanged?.({ property: property as Property, links: propertyLinks })
  let refreshFunction = () => {}
  const archivedUnitsParam = "archived_units"
  let showArchivedUnits = $state(
    untrack(
      () =>
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get(archivedUnitsParam) === "1",
    ),
  )
  const visibleUnits = $derived(
    (property?.units ?? []).filter((unit) => showArchivedUnits || !unit.effective_archived_at),
  )

  const isLinkUnitArchived = (link: PropertyCustomer) =>
    Boolean(
      link.property_unit_id &&
      property?.units.find((unit) => unit.id === link.property_unit_id)?.effective_archived_at,
    )

  const toggleArchivedUnits = () => {
    showArchivedUnits = !showArchivedUnits

    if (typeof window === "undefined") return

    const params = new URLSearchParams(window.location.search)

    if (showArchivedUnits) params.set(archivedUnitsParam, "1")
    else params.delete(archivedUnitsParam)

    const search = params.toString()
    replaceUrlWithoutRoutePublish(
      `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`,
    )
  }

  const handleUnitSaved = async (payload: UnitFormModel) => {
    if (!property || !canEdit) return

    const basePayload = {
      unit_label: payload.unit_label.trim() || "Einheit",
      unit_number: payload.unit_number?.trim() || null,
      notes: payload.notes ?? null,
    }
    try {
      let saved: PropertyUnit

      if (payload.id) {
        saved = await updatePropertyUnit(payload.id, basePayload)
        property = {
          ...property,
          units: (property.units ?? []).map((unit) => (unit.id === saved.id ? saved : unit)),
        }
      } else {
        saved = await createPropertyUnit({ property_id: property.id, ...basePayload })
        property = {
          ...property,
          units: [...(property.units ?? []), saved],
        }
      }

      syncpropertyLinks()
      newNotification({ class: "success", html: "Einheit gespeichert." })
      refreshFunction()
    } catch (err) {
      console.error("property_unit_save_failed", err)
      newNotification({ class: "error", html: "Einheit konnte nicht gespeichert werden." })
    }
  }

  const handleArchiveUnit = async (unit: PropertyUnitRow) => {
    if (!property || !unit.id || !canEdit) return

    await newApproval({
      title: "Einheit archivieren",
      description:
        "Diese Einheit aus neuen Suchen und Zuordnungen ausblenden? Bestehende Aufträge und Zuordnungen bleiben erhalten.",
      approvalLabel: "Ja, archivieren",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        try {
          const response = await archivePropertyUnitsBatch([unit.id])

          if (!registryArchiveProcessed(response, unit.id)) {
            newNotification({
              class: "warning",
              html: registryArchiveIssueMessage(
                response,
                unit.id,
                "Einheit wurde nicht archiviert.",
              ),
            })

            return
          }

          property = {
            ...property,
            units: (property.units ?? []).map((entry) =>
              entry.id === unit.id
                ? {
                    ...entry,
                    archived_at: new Date().toISOString(),
                    archive_reason: "USER_REQUEST",
                    effective_archived_at: new Date().toISOString(),
                    effective_archive_reason: "USER_REQUEST",
                    archive_source: "SELF",
                  }
                : entry,
            ),
          }
          syncpropertyLinks()
          newNotification({ class: "success", html: "Einheit archiviert." })
        } catch (err) {
          console.error("property_unit_archive_failed", err)
          newNotification({ class: "error", html: "Einheit konnte nicht archiviert werden." })
        }
      },
    })
  }

  const handleRestoreUnit = async (unit: PropertyUnitRow) => {
    if (!property || !unit.id || !canEdit) return

    try {
      const response = await restorePropertyUnitsBatch([unit.id])

      if (!registryArchiveProcessed(response, unit.id)) {
        newNotification({
          class: "warning",
          html: registryArchiveIssueMessage(
            response,
            unit.id,
            "Einheit wurde nicht wiederhergestellt.",
          ),
        })

        return
      }

      property = {
        ...property,
        units: (property.units ?? []).map((entry) =>
          entry.id === unit.id
            ? {
                ...entry,
                archived_at: null,
                archive_reason: null,
                effective_archived_at: null,
                effective_archive_reason: null,
                archive_source: null,
              }
            : entry,
        ),
      }
      syncpropertyLinks()
      newNotification({ class: "success", html: "Einheit wiederhergestellt." })
    } catch (err) {
      console.error("property_unit_restore_failed", err)
      newNotification({ class: "error", html: "Einheit konnte nicht wiederhergestellt werden." })
    }
  }

  const openUnitOverlay = (unit?: PropertyUnit | null) => {
    if (!property || !organizationId || !canEdit) return

    openOverlay({
      title: unit ? "Einheit bearbeiten" : "Einheit hinzufügen",
      content: PropertyUnitOverlay,
      properties: {
        unit,
        organizationId,
        darkMode: false,
        customers,
        showCustomerAssignment: false,
        onSaved: handleUnitSaved,
      },
    })
  }

  const handleContactsLinked = (createdLinks: PropertyCustomer[]) => {
    if (!createdLinks.length || !property) return

    propertyLinks = [...propertyLinks, ...createdLinks]
    syncpropertyLinks()
    refreshFunction()
  }

  const openContactAssignmentOverlay = (unit: PropertyUnitRow) => {
    if (!property || !unit.id || !canEdit || unit.effective_archived_at) return

    openOverlay({
      title: "Ansprechpartner zuordnen",
      content: UnitContactAssignmentOverlay,
      properties: {
        organizationId,
        property,
        unit,
        customers,
        propertyLinks,
        onLinked: handleContactsLinked,
      },
    })
  }

  const updateLinkRole = async (link: PropertyCustomer, role: PropertyCustomerRole) => {
    if (!property || !canEdit || isLinkUnitArchived(link)) return

    try {
      const updated = await updatePropertyCustomer(link.id, { role })
      propertyLinks = propertyLinks.map((entry) =>
        entry.id === link.id ? { ...entry, ...updated } : entry,
      )
      syncpropertyLinks()
      newNotification({ class: "success", html: "Rolle gespeichert." })
    } catch (err) {
      console.error("property_unit_contact_role_update_failed", err)
      newNotification({ class: "error", html: "Rolle konnte nicht gespeichert werden." })
    }
  }

  const updateLinkOutreach = async (link: PropertyCustomer, outreachEnabled: boolean) => {
    if (!property || !canEdit || isLinkUnitArchived(link)) return

    try {
      const updated = await updatePropertyCustomer(link.id, { outreach_enabled: outreachEnabled })
      propertyLinks = propertyLinks.map((entry) =>
        entry.id === link.id ? { ...entry, ...updated } : entry,
      )
      syncpropertyLinks()
      newNotification({ class: "success", html: "Ansprechen gespeichert." })
    } catch (err) {
      console.error("property_unit_contact_outreach_update_failed", err)
      newNotification({ class: "error", html: "Ansprechen konnte nicht gespeichert werden." })
    }
  }

  const removeLink = async (link: PropertyCustomer) => {
    if (!property || !canEdit || isLinkUnitArchived(link)) return

    await newApproval({
      title: "Zuordnung entfernen?",
      description:
        "Der Ansprechpartner bleibt erhalten, wird aber von dieser Einheit getrennt und für diese Einheit nicht mehr automatisch angesprochen.",
      approvalLabel: "Entfernen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        try {
          await deletePropertyCustomer(link.id)
          propertyLinks = propertyLinks.filter((entry) => entry.id !== link.id)
          syncpropertyLinks()
          refreshFunction()
          newNotification({ class: "success", html: "Zuordnung entfernt." })
        } catch (err) {
          console.error("property_unit_contact_link_delete_failed", err)
          newNotification({ class: "error", html: "Zuordnung konnte nicht entfernt werden." })
        }
      },
    })
  }

  const openCustomer = (customer: Customer) => {
    if (!customer?.id || !organizationId) return

    spaNavigate(
      `/organizations/${encodeURIComponent(organizationId)}/customers/${encodeURIComponent(customer.id)}/details`,
    )
  }
</script>

{#if !property}
  <div class="state-block">
    <p>Keine Liegenschaft geladen.</p>
  </div>
{:else}
  <SectionTitle
    title="Einheiten"
    styleOuter="margin-bottom: 1.2rem;"
    helpModalTextId="propertyDetailUnits"
  >
    {#snippet buttonSnippet()}
      <CTA
        type="tertiaryButton"
        content={showArchivedUnits ? "Nur aktive" : "Archivierte anzeigen"}
        darkMode={false}
        onClick={toggleArchivedUnits}
      >
        {#snippet iconSnippet()}<Icon path={mdiArchiveEye} />{/snippet}
      </CTA>
      {#if canEdit}
        <CTA
          type="tertiaryButton"
          content=""
          darkMode={false}
          tooltipContent={customerRegistryActionTooltips.unitAdd}
          tooltipClassName={CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS}
          onClick={() => openUnitOverlay(null)}
        >
          {#snippet iconSnippet()}
            <Icon path={mdiPlus} />
          {/snippet}
        </CTA>
      {/if}
    {/snippet}
  </SectionTitle>
  <PropertyUnitsTable
    units={visibleUnits}
    {customers}
    links={propertyLinks}
    setRefreshFunction={(fn) => (refreshFunction = fn)}
    showCustomers={true}
    showActions={canEdit}
    onEdit={(unit) => openUnitOverlay(unit as PropertyUnit)}
    onArchive={handleArchiveUnit}
    onRestore={handleRestoreUnit}
    onAssignContacts={(unit) => openContactAssignmentOverlay(unit)}
    onUpdateLinkRole={updateLinkRole}
    onUpdateLinkOutreach={updateLinkOutreach}
    onRemoveLink={removeLink}
    onOpenCustomer={openCustomer}
    disabled={!canEdit}
    emptyLabel="Noch keine Einheiten hinterlegt."
  />
{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .state-block {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
</style>
